import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, FolderOpen, Loader2, ShieldCheck, Sparkles, X } from 'lucide-react'
import GlassCard from '@/components/GlassCard'
import { auditsApi } from '@/api/client'
import type { SuggestionPlan } from '@/api/client'
import { gatherExcerpts, isFolderPickSupported, pickSourceFolder } from '@/lib/sourceWalker'
import type { GatheredCheck } from '@/lib/sourceWalker'

type Phase = 'idle' | 'gathering' | 'review' | 'submitting'

/**
 * Post-audit source attachment (REFACTOR_FILE_ACCESS.md): pick a local folder,
 * gather only the excerpts the suggestion plan asks for, show the user exactly
 * what will be shared, then submit. Nothing is read before the picker grant and
 * nothing leaves the machine before the user confirms the review list.
 */
export default function SourceAttachPanel({
  auditId,
  onStarted,
}: {
  auditId: string
  onStarted: () => void
}) {
  const [phase, setPhase] = useState<Phase>('idle')
  const [plan, setPlan] = useState<SuggestionPlan | null>(null)
  const [gathered, setGathered] = useState<GatheredCheck[]>([])
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)

  if (!isFolderPickSupported()) {
    return null // Firefox/Safari fallback is a deferred follow-up
  }

  async function start() {
    setError(null)
    try {
      const fetchedPlan = await auditsApi.getSuggestionPlan(auditId)
      if (fetchedPlan.checks.length === 0) {
        setError('Nothing to suggest — every audit check is passing.')
        return
      }
      const folder = await pickSourceFolder()
      if (!folder) return // user cancelled the picker
      setPhase('gathering')
      const checks = await gatherExcerpts(folder, fetchedPlan)
      setPlan(fetchedPlan)
      setGathered(checks)
      setPhase('review')
    } catch (err) {
      setPhase('idle')
      setError(err instanceof Error ? err.message : 'Could not read the selected folder.')
    }
  }

  async function submit() {
    if (!plan) return
    setPhase('submitting')
    setError(null)
    try {
      await auditsApi.generateSuggestions(auditId, {
        checks: gathered.map(c => ({
          auditCheckId: c.auditCheckId,
          files: c.files.map(f => ({ path: f.path, content: f.content, truncated: f.truncated })),
        })),
      })
      setPhase('idle')
      setPlan(null)
      setGathered([])
      onStarted()
    } catch (err) {
      setPhase('review')
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      setError(message ?? 'Could not start suggestion generation.')
    }
  }

  function cancelReview() {
    setPhase('idle')
    setPlan(null)
    setGathered([])
    setError(null)
  }

  const uniqueFiles = new Map<string, number>()
  for (const check of gathered) {
    for (const f of check.files) uniqueFiles.set(f.path, f.bytes)
  }
  const totalBytes = [...uniqueFiles.values()].reduce((a, b) => a + b, 0)
  const checksWithFiles = gathered.filter(c => c.files.length > 0)

  return (
    <GlassCard className="p-5">
      {(phase === 'idle' || phase === 'gathering') && (
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Sparkles size={14} className="text-accent" /> AI fix suggestions
            </h3>
            <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
              Point Lens at this site's source folder to get targeted fix suggestions.
              Only small, relevant excerpts are shared — you review them first.
            </p>
          </div>
          <button
            type="button"
            onClick={start}
            disabled={phase === 'gathering'}
            className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium
                       bg-accent/15 border border-accent/40 text-accent hover:bg-accent/25
                       transition-colors disabled:opacity-60"
          >
            {phase === 'gathering'
              ? <><Loader2 size={13} className="animate-spin" /> Scanning…</>
              : <><FolderOpen size={13} /> Attach source folder</>}
          </button>
        </div>
      )}

      {(phase === 'review' || phase === 'submitting') && plan && (
        <div>
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <ShieldCheck size={14} className="text-accent" /> Review what will be shared
              </h3>
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                {uniqueFiles.size} file{uniqueFiles.size === 1 ? '' : 's'} ·{' '}
                {formatBytes(totalBytes)} across {checksWithFiles.length} audit check
                {checksWithFiles.length === 1 ? '' : 's'}. Nothing else leaves your machine.
              </p>
            </div>
            <button
              type="button"
              onClick={cancelReview}
              disabled={phase === 'submitting'}
              aria-label="Cancel"
              className="p-1 rounded hover:bg-white/10 transition-colors"
              style={{ color: 'var(--text-secondary)' }}
            >
              <X size={14} />
            </button>
          </div>

          <button
            type="button"
            onClick={() => setExpanded(e => !e)}
            className="flex items-center gap-1.5 text-xs mb-2 hover:text-accent transition-colors"
            style={{ color: 'var(--text-secondary)' }}
          >
            <motion.span animate={{ rotate: expanded ? 180 : 0 }} className="inline-flex">
              <ChevronDown size={12} />
            </motion.span>
            {expanded ? 'Hide file list' : 'Show file list'}
          </button>

          <AnimatePresence initial={false}>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="max-h-56 overflow-y-auto rounded-md bg-white/5 border border-white/10 p-3 mb-3 space-y-2">
                  {checksWithFiles.map(check => (
                    <div key={check.auditCheckId}>
                      <p className="text-[11px] font-medium mb-0.5">{check.title}</p>
                      {check.files.map(f => (
                        <p key={f.path} className="text-[11px] font-mono truncate"
                          style={{ color: 'var(--text-secondary)' }}>
                          {f.path} · {formatBytes(f.bytes)}{f.truncated ? ' (truncated)' : ''}
                        </p>
                      ))}
                    </div>
                  ))}
                  {checksWithFiles.length === 0 && (
                    <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                      No files in the folder matched this report's audit checks — suggestions
                      will be generated from the audit findings alone.
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={phase === 'submitting'}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium
                         bg-accent/15 border border-accent/40 text-accent hover:bg-accent/25
                         transition-colors disabled:opacity-60"
            >
              {phase === 'submitting'
                ? <><Loader2 size={13} className="animate-spin" /> Starting…</>
                : <><Sparkles size={13} /> Generate suggestions</>}
            </button>
            <button
              type="button"
              onClick={cancelReview}
              disabled={phase === 'submitting'}
              className="px-3 py-2 rounded-md text-xs font-medium border bg-white/5 border-white/10
                         hover:border-white/20 transition-colors"
              style={{ color: 'var(--text-secondary)' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <p className="text-xs text-red-400 mt-3">{error}</p>
      )}
    </GlassCard>
  )
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  return `${(n / 1024).toFixed(1)} KB`
}
